import {
  Button,
  CloseButton,
  Dialog,
  Field,
  Input,
  Text,
} from '@chakra-ui/react'
import React from 'react'
import { DataTag } from '../DataTag/DataTag'

type DeleteClusterModalProps = {
  clusterName: string | undefined
  isOpen: boolean
  onClose: () => void
  handleDelete: () => void
}

export function DeleteClusterModal({
  clusterName,
  isOpen,
  onClose,
  handleDelete,
}: DeleteClusterModalProps) {
  const [repeatClusterName, setRepeatClusterName] = React.useState('')
  const [hasTriedToSubmit, setHasTriedToSubmit] = React.useState(false)

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (repeatClusterName !== clusterName) {
      setHasTriedToSubmit(true)
      return
    }

    handleDelete()
  }

  if (!clusterName) {
    return <></>
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content as="form" onSubmit={handleSubmit}>
          <Dialog.Header pb={3}>
            <Dialog.Title>Delete HiveMQ Cluster</Dialog.Title>
            <Dialog.CloseTrigger asChild>
              <CloseButton />
            </Dialog.CloseTrigger>
          </Dialog.Header>
          <Dialog.Body>
            <Text fontSize="1rem">
              This action permanently deletes this cluster.
              <br />
              All data on the cluster and the cluster itself are deleted.
            </Text>

            <Text
              fontWeight="900"
              color="text.text-error-strong"
              fontSize="1rem"
            >
              This action cannot be undone.
            </Text>

            <Field.Root
              invalid={hasTriedToSubmit && repeatClusterName !== clusterName}
              mt={4}
            >
              <Field.Label>
                Please type the cluster name to confirm:
              </Field.Label>
              <DataTag
                value={clusterName}
                data-testid="delete-cluster-name"
                mx={1}
                my={2}
                whiteSpace="noWrap"
              >
                {clusterName}
              </DataTag>
              <Input
                name="cluster"
                onChange={(event) => {
                  setRepeatClusterName(event.target.value)
                }}
                value={repeatClusterName}
                placeholder="Cluster name"
                size="lg"
                borderColor="surface.500"
                borderWidth="1px"
                required
              />
              <Field.ErrorText>
                Input does not match the above cluster name
              </Field.ErrorText>
            </Field.Root>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="solid"
              colorPalette="red"
              type="submit"
              data-testid="dangerous-action-confirm"
              disabled={repeatClusterName !== clusterName && !hasTriedToSubmit}
            >
              Delete Cluster
            </Button>
          </Dialog.Footer>
          <Dialog.CloseTrigger />
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  )
}
